import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListItem, ListItemText } from '@material-ui/core';
import cx from 'classnames';
import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { useObserver } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { translate } from '../../../../../../common/intl';
import { MomentFormats, useDate } from '../../../../../../common/utils/date';
import DocumentRevisionDialog from '../../../../../components/common/dialogs/DocumentRevisionDialog';
import { Autocomplete, FormikField, IconButton } from '../../../../../components/forms/fields-next';
import { OptionType } from '../../../../../components/forms/fields/Autocomplete/types';
import { StyleTooltip } from '../../../../../dashboard.new/line.items/common/StyleTooltip';
import useDialog from '../../../../../hooks/useDialog';
import useHover from '../../../../../hooks/useHover';
import { EditableEquipmentItem } from '../../../types';
import { ADD_FIELD, IS_ACTUAL_ID_ADDITION, MIN_SEARCH_TERM_LENGTH, MODE_FIELD } from '../constants';
import { getEquipmentOption, useFetchData } from '../hooks/useFetchData';
import { isEditEquipItem } from '../utils';
import useStyles from './styles';
import TreeListCellWrapper from './TreelistCellWrapper';
import { customEQOptionType, CustomTreeListCellProps } from './types';

interface LotAutocompleteProps {
  field?: string
  dataItem: EditableEquipmentItem
  actualId?: string
  isEBProcedure?: boolean
}

const LotAutocomplete: React.FC<LotAutocompleteProps> = ({
  field = '',
  dataItem,
  actualId,
  isEBProcedure,
}) => {
  const { setFieldValue, errors, getFieldProps, values } = useFormikContext<EditableEquipmentItem>();
  const classes = useStyles();
  const [searchText, setSearchText] = useState('');
  const [inputText, setInputText] = useState<string>();
  const { getFormattedDateString, convertDateTimeToUserTz } = useDate();
  const isFreeSolo = isEBProcedure;
  const { equipmentOptions, allowedEditEqOptions } = useFetchData();

  const draftValue = values[field] === undefined ? dataItem[field] : values[field];
  const selectedId = typeof draftValue === 'string' ? draftValue : '';
  const value = useMemo(() => equipmentOptions.find((option) => option.value === selectedId)
    ?? { label: selectedId, value: selectedId }, [equipmentOptions, selectedId]);
  const items = useMemo(() => isFreeSolo && searchText.length >= MIN_SEARCH_TERM_LENGTH
    ? allowedEditEqOptions.filter((option) => option.label.toLowerCase().includes(searchText.toLowerCase()))
    : allowedEditEqOptions, [allowedEditEqOptions, isFreeSolo, searchText]);
  const updateSearch = useMemo(() => debounce(setSearchText, 1000, { leading: true, trailing: true }), []);

  useEffect(() => () => updateSearch.cancel(), [updateSearch]);

  const updateDraft = (equipmentId: string) => {
    field && setFieldValue(field, equipmentId);
    setFieldValue(IS_ACTUAL_ID_ADDITION, true);
    setFieldValue('parentEquipPlannedId', dataItem?.parentEquipPlannedId);
    setFieldValue('parentId', dataItem?.parentId);
    setFieldValue('id', dataItem.id);
    setFieldValue(ADD_FIELD, dataItem[ADD_FIELD]);
  };

  const handleChange = (_, option?: OptionType) => {
    updateSearch.cancel();
    setSearchText('');
    setInputText(undefined);
    updateDraft(option?.value ?? '');
  };

  const onInputChange = (_: React.ChangeEvent<{}>, inputVal: string, reason: string) => {
    if (reason !== 'input') {
      return;
    }

    setInputText(inputVal);
    updateDraft(isFreeSolo ? inputVal : '');
    if (!inputVal) {
      updateSearch.cancel();
      setSearchText('');
    } else {
      updateSearch(inputVal);
    }
  };

  return (
    <FormikField name={field} required error={Boolean(errors[field])}>
      <Autocomplete
        {...getFieldProps(field)}
        value={value}
        inputValue={inputText ?? value.label}
        disabled={!!actualId}
        options={items}
        blurOnSelect
        freeSolo={isFreeSolo}
        onInputChange={onInputChange}
        disableOpenOnFocus
        placeholder={translate('common.select')}
        size="small"
        getOptionLabel={(opt) => opt.label}
        forcePopupIcon
        renderOption={(option: customEQOptionType, props) => (
          <ListItem {...props} disableGutters className={classes.listItemOption}>
            <ListItemText
              className={classes.listItemOption}
              primary={option.label}
              secondary={
                <div className={classes.dateInfo}>
                  {option.calDueDate && (
                    <span>
                      {translate('form.builder.lhrs.procedure.select.equipment.option.cal.due', {
                        date: getFormattedDateString(convertDateTimeToUserTz(option.calDueDate, MomentFormats.MonthDateYear), MomentFormats.BriefDate),
                      })}
                    </span>
                  )}
                  {option.calDueDate && option.pmDueDate && '  |  '}
                  {option.pmDueDate && (
                    <span>
                      {translate('form.builder.lhrs.procedure.select.equipment.option.pm.due', {
                        date: getFormattedDateString(convertDateTimeToUserTz(option.pmDueDate, MomentFormats.MonthDateYear), MomentFormats.BriefDate),
                      })}
                    </span>
                  )}
                </div>
              }
            />
          </ListItem>
        )}
        onChange={handleChange}
      />
    </FormikField>
  );
};

const EquipmentIdCell: React.FC<CustomTreeListCellProps> = (props) => {
  const {
    hasChildren,
    dataItem,
    field,
    addActualId,
    isDisabled,
    isEnableActualIdAddition,
    isEBProcedure,
  } = props;
  const { parentEquipPlannedId, actualId } = dataItem;
  const isEditMode = isEBProcedure
    ? isEditEquipItem(dataItem) && (dataItem.isActualIdAddition || dataItem.equipmentId)
    : isEditEquipItem(dataItem);
  const classes = useStyles({ hasChildren, isEditMode });
  const [addLotRef, isAddLotHovered] = useHover<HTMLButtonElement>();
  const quickViewDialog = useDialog();
  const catalogOption = useObserver(() => getEquipmentOption(dataItem[field ?? '']));
  const displayValue = catalogOption?.label ?? dataItem[field ?? ''] ?? '';
  const isAddActualIdVisible
    = dataItem?.isActualIdAddition || !isEnableActualIdAddition || dataItem.equipmentId
      ? false
      : !(dataItem[MODE_FIELD] || dataItem[ADD_FIELD]);

  if (isAddActualIdVisible) {
    if (isDisabled) {
      return <TreeListCellWrapper {...props}><></> </TreeListCellWrapper>;
    }

    return (
      <TreeListCellWrapper {...props}>
        <span className={cx(classes.defaultIcon)}>
          <StyleTooltip title={translate('common.add')} placement="top" arrow>
            <IconButton kind="simple" size="small" ref={addLotRef} disabled={isDisabled} data-cy="add-lot-button">
              <FontAwesomeIcon
                size="sm"
                className={cx(classes.addChildLotIcon, { [classes.disabled]: isDisabled })}
                onClick={() => addActualId?.(dataItem)}
                icon={isAddLotHovered ? solid('circle-plus') : regular('circle-plus')}
              />
            </IconButton>
          </StyleTooltip>
        </span>
      </TreeListCellWrapper>
    );
  }

  return (
    <TreeListCellWrapper {...props}>
      <span className={cx(classes.lotNameCell, classes.titleInfoCell, hasChildren ? classes.boldText : classes.normalText)}>
        <span className={cx({ [classes.lotInfoId]: !isEditMode })}>
          {isEditMode ? (
            <LotAutocomplete
              key={`${dataItem.id}:${field}`}
              field={field}
              dataItem={dataItem}
              actualId={actualId}
              isEBProcedure={isEBProcedure}
            />
          ) : (
            displayValue
          )}
        </span>
      </span>
      <DocumentRevisionDialog dialog={quickViewDialog} {...{ docRevId: actualId, parentRevId: parentEquipPlannedId }} />
    </TreeListCellWrapper>
  );
};

export default EquipmentIdCell;
